import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { LucideIconInput } from '@lucide/angular';
import { ButtonSizes } from '../buttons/default/button.component';
import { IconComponent } from '../icon/icon.component';

/**
 * A chip is a pill-shaped `ptl-button` that stays pressed, so it borrows the button's size scale
 * verbatim (metrics in the SCSS, icon plumbing in the template) rather than growing its own. Only the
 * sizes a chip plausibly needs are exposed.
 */
export type ToggleChipSize = Extract<ButtonSizes, 'xs' | 's' | 'sm' | 'm'>;

@Component({
  selector: 'ptl-toggle-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IconComponent],
  templateUrl: './toggle-chip.component.html',
  styleUrls: ['./toggle-chip.component.scss'],
})
export class ToggleChipComponent {
  label = input('');
  active = input(false);
  color = input('var(--accent)');
  size = input<ToggleChipSize>('xs');
  fullWidth = input(false);
  /** When false, `label()` is exposed only as the button's accessible name (aria-label/title), not as visible text. */
  showLabel = input(true);
  icon = input<LucideIconInput | undefined>(undefined);
  /** Optional swap while active — falls back to `icon()`. */
  activeIcon = input<LucideIconInput | undefined>(undefined);
  clicked = output<void>();

  protected readonly currentIcon = computed(() => (this.active() ? (this.activeIcon() ?? this.icon()) : this.icon()));
}
